import React, { useState, useEffect } from 'react';
import ClientProvider from '../../../Various/ClientProvider';
import { formatValue } from '../utils/formatters';

const AddContactModal = ({ isOpen, onClose, action, existingContacts, websiteHash, onSuccess }) => {
    const wsProvider = React.useContext(ClientProvider);
    const [selectedTemplateContact, setSelectedTemplateContact] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // Contact form fields based on ContactDetailModal structure
    const [formData, setFormData] = useState({
        salutation: '',
        firstname: '',
        lastname: '',
        role: '',
        email: '',
        phone: '',
        whatsapp: '',
        linkedin: '',
        website: '',
        domain: '',
        postal_address: '',
        other_contact_info: ''
    });

    // Reset form when modal opens/closes or template changes
    useEffect(() => {
        if (isOpen) {
            if (selectedTemplateContact) {
                // Prefill from selected contact
                setFormData({
                    salutation: selectedTemplateContact.salutation || '',
                    firstname: selectedTemplateContact.firstname || '',
                    lastname: selectedTemplateContact.lastname || '',
                    role: selectedTemplateContact.role || '',
                    email: selectedTemplateContact.email || '',
                    phone: selectedTemplateContact.phone || '',
                    whatsapp: selectedTemplateContact.whatsapp || '',
                    linkedin: selectedTemplateContact.linkedin || '',
                    website: selectedTemplateContact.website || '',
                    domain: selectedTemplateContact.domain || '',
                    postal_address: selectedTemplateContact.postal_address || '',
                    other_contact_info: selectedTemplateContact.other_contact_info || ''
                });
            } else {
                // Reset to empty
                setFormData({
                    salutation: '',
                    firstname: '',
                    lastname: '',
                    role: '',
                    email: '',
                    phone: '',
                    whatsapp: '',
                    linkedin: '',
                    website: '',
                    domain: '',
                    postal_address: '',
                    other_contact_info: ''
                });
            }
        }
    }, [isOpen, selectedTemplateContact]);

    if (!isOpen) {
        return null;
    }

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSubmit = () => {
        if (!websiteHash) {
            console.error('No website hash provided');
            return;
        }

        setIsSubmitting(true);

        // Call websocket function
        wsProvider.ws.waitForSocketAndSend({
            action: "doWebsiteManualAction",
            options: {
                url_hash: websiteHash,
                type: "add_contact",
                contact: formData
            }
        }, (response) => {
            setIsSubmitting(false);
            console.log("doWebsiteManualAction response", response);
            if (onSuccess) {
                onSuccess(response);
            }
            onClose();
        });
    };

    const getContactName = (contact) => {
        if (contact.firstname || contact.lastname) {
            return `${contact.firstname || ''} ${contact.lastname || ''}`.trim() || 'Unnamed Contact';
        }
        if (contact.role) {
            return contact.role;
        }
        if (contact.email) {
            return contact.email;
        }
        if (contact.phone) {
            return contact.phone;
        }
        return 'Unnamed Contact';
    };

    const getContactSummary = (contact) => {
        const parts = [];
        if (contact.email) parts.push(`📧 ${contact.email}`);
        if (contact.phone) parts.push(`📞 ${contact.phone}`);
        if (contact.role) parts.push(`💼 ${contact.role}`);
        return parts.length > 0 ? parts.join(' • ') : 'No contact info';
    };

    const contactsList = existingContacts?.contacts || [];

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(7, 7, 10, 0.92)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 10000,
                padding: '24px'
            }}
            onClick={onClose}
        >
            <div
                style={{
                    position: 'relative',
                    width: '100%',
                    maxWidth: '900px',
                    maxHeight: '90vh',
                    backgroundColor: '#121212',
                    borderRadius: '10px',
                    boxShadow: '0 18px 60px rgba(0, 0, 0, 0.55)',
                    border: '1px solid rgba(100, 181, 246, 0.35)',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={onClose}
                    style={{
                        position: 'absolute',
                        top: '14px',
                        right: '14px',
                        backgroundColor: 'rgba(220, 53, 69, 0.92)',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '50%',
                        width: '42px',
                        height: '42px',
                        fontSize: '24px',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        fontWeight: 'bold',
                        boxShadow: '0 8px 20px rgba(220, 53, 69, 0.45)',
                        transition: 'transform 0.2s ease, background-color 0.2s ease',
                        zIndex: 1
                    }}
                    onMouseEnter={(e) => {
                        e.target.style.backgroundColor = 'rgba(207, 46, 60, 0.95)';
                        e.target.style.transform = 'scale(1.05)';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'rgba(220, 53, 69, 0.92)';
                        e.target.style.transform = 'scale(1)';
                    }}
                >
                    ×
                </button>
                <div
                    style={{
                        flex: 1,
                        overflow: 'auto',
                        backgroundColor: '#0c0c0c',
                        padding: '24px'
                    }}
                >
                    <h3
                        style={{
                            margin: '0 0 24px 0',
                            color: '#e0e0e0',
                            fontWeight: '600',
                            fontSize: '20px'
                        }}
                    >
                        Add Contact Manually
                    </h3>

                    {action?.reason && (
                        <div style={{
                            backgroundColor: '#181818',
                            border: '1px solid rgba(239, 68, 68, 0.3)',
                            borderRadius: '8px',
                            padding: '16px',
                            marginBottom: '24px'
                        }}>
                            <div style={{
                                fontSize: '12px',
                                color: '#909090',
                                textTransform: 'uppercase',
                                marginBottom: '6px',
                                fontWeight: '600',
                                letterSpacing: '0.5px'
                            }}>
                                Reason
                            </div>
                            <div style={{
                                fontSize: '14px',
                                color: '#e0e0e0'
                            }}>
                                {formatValue(action.reason)}
                            </div>
                        </div>
                    )}

                    {/* Template Selection */}
                    {contactsList.length > 0 && (
                        <div style={{
                            backgroundColor: '#181818',
                            border: '1px solid rgba(100, 181, 246, 0.12)',
                            borderRadius: '8px',
                            padding: '20px',
                            marginBottom: '24px'
                        }}>
                            <div style={{
                                fontSize: '14px',
                                color: '#d0d0d0',
                                fontWeight: '600',
                                marginBottom: '16px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                            }}>
                                Start from Existing Contact (Optional)
                            </div>
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px',
                                maxHeight: '200px',
                                overflowY: 'auto'
                            }}>
                                <div
                                    onClick={() => setSelectedTemplateContact(null)}
                                    style={{
                                        backgroundColor: selectedTemplateContact === null ? '#2563eb' : '#0c0c0c',
                                        border: `1px solid ${selectedTemplateContact === null ? '#3b82f6' : 'rgba(100, 181, 246, 0.12)'}`,
                                        borderRadius: '6px',
                                        padding: '12px',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                        if (selectedTemplateContact !== null) {
                                            e.currentTarget.style.backgroundColor = '#1a1a1a';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (selectedTemplateContact !== null) {
                                            e.currentTarget.style.backgroundColor = '#0c0c0c';
                                        }
                                    }}
                                >
                                    <div style={{
                                        fontSize: '14px',
                                        fontWeight: '600',
                                        color: '#e0e0e0'
                                    }}>
                                        Start from Empty
                                    </div>
                                </div>
                                {contactsList.map((contact, index) => (
                                    <div
                                        key={index}
                                        onClick={() => setSelectedTemplateContact(contact)}
                                        style={{
                                            backgroundColor: selectedTemplateContact === contact ? '#2563eb' : '#0c0c0c',
                                            border: `1px solid ${selectedTemplateContact === contact ? '#3b82f6' : 'rgba(100, 181, 246, 0.12)'}`,
                                            borderRadius: '6px',
                                            padding: '12px',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                            if (selectedTemplateContact !== contact) {
                                                e.currentTarget.style.backgroundColor = '#1a1a1a';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (selectedTemplateContact !== contact) {
                                                e.currentTarget.style.backgroundColor = '#0c0c0c';
                                            }
                                        }}
                                    >
                                        <div style={{
                                            fontSize: '14px',
                                            fontWeight: '600',
                                            color: '#e0e0e0',
                                            marginBottom: '4px'
                                        }}>
                                            {getContactName(contact)}
                                        </div>
                                        <div style={{
                                            fontSize: '12px',
                                            color: '#a0a0a0'
                                        }}>
                                            {getContactSummary(contact)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Contact Form */}
                    <div style={{
                        backgroundColor: '#181818',
                        border: '1px solid rgba(100, 181, 246, 0.12)',
                        borderRadius: '8px',
                        padding: '20px',
                        marginBottom: '24px'
                    }}>
                        <div style={{
                            fontSize: '14px',
                            color: '#d0d0d0',
                            fontWeight: '600',
                            marginBottom: '16px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                        }}>
                            Contact Information
                        </div>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                            gap: '16px'
                        }}>
                            {[
                                { key: 'salutation', label: 'Salutation' },
                                { key: 'firstname', label: 'First Name' },
                                { key: 'lastname', label: 'Last Name' },
                                { key: 'role', label: 'Role' },
                                { key: 'email', label: 'Email', type: 'email' },
                                { key: 'phone', label: 'Phone', type: 'tel' },
                                { key: 'whatsapp', label: 'WhatsApp', type: 'tel' },
                                { key: 'linkedin', label: 'LinkedIn', type: 'url' },
                                { key: 'website', label: 'Website', type: 'url' },
                                { key: 'domain', label: 'Domain' },
                                { key: 'postal_address', label: 'Postal Address', fullWidth: true },
                                { key: 'other_contact_info', label: 'Other Contact Info', fullWidth: true }
                            ].map(field => (
                                <div key={field.key} style={{
                                    gridColumn: field.fullWidth ? '1 / -1' : 'auto'
                                }}>
                                    <label style={{
                                        display: 'block',
                                        fontSize: '12px',
                                        color: '#909090',
                                        marginBottom: '6px',
                                        fontWeight: '600',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}>
                                        {field.label}
                                    </label>
                                    <input
                                        type={field.type || 'text'}
                                        value={formData[field.key] || ''}
                                        onChange={(e) => handleInputChange(field.key, e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '10px',
                                            backgroundColor: '#0c0c0c',
                                            border: '1px solid rgba(100, 181, 246, 0.12)',
                                            borderRadius: '6px',
                                            color: '#e0e0e0',
                                            fontSize: '14px',
                                            fontFamily: 'inherit'
                                        }}
                                        onFocus={(e) => {
                                            e.target.style.borderColor = 'rgba(100, 181, 246, 0.5)';
                                        }}
                                        onBlur={(e) => {
                                            e.target.style.borderColor = 'rgba(100, 181, 246, 0.12)';
                                        }}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: '12px',
                        paddingTop: '16px',
                        borderTop: '1px solid rgba(255, 255, 255, 0.08)'
                    }}>
                        <button
                            onClick={onClose}
                            disabled={isSubmitting}
                            style={{
                                backgroundColor: '#3a3a3a',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '6px',
                                padding: '10px 20px',
                                fontSize: '14px',
                                fontWeight: '600',
                                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                opacity: isSubmitting ? 0.6 : 1
                            }}
                            onMouseEnter={(e) => {
                                if (!isSubmitting) {
                                    e.target.style.backgroundColor = '#4a4a4a';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (!isSubmitting) {
                                    e.target.style.backgroundColor = '#3a3a3a';
                                }
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                            style={{
                                backgroundColor: '#10b981',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '6px',
                                padding: '10px 20px',
                                fontSize: '14px',
                                fontWeight: '600',
                                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                opacity: isSubmitting ? 0.6 : 1,
                                boxShadow: isSubmitting ? 'none' : '0 4px 12px rgba(16, 185, 129, 0.35)'
                            }}
                            onMouseEnter={(e) => {
                                if (!isSubmitting) {
                                    e.target.style.backgroundColor = '#059669';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (!isSubmitting) {
                                    e.target.style.backgroundColor = '#10b981';
                                }
                            }}
                        >
                            {isSubmitting ? 'Saving...' : 'Save Contact'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddContactModal;


